import { AhcrCriterionTypeEnum, AnprCriterionTypeEnum } from '../types/alarm.types'
import {
  alarmColors,
  genderOptions,
  sleeveLengthOptions,
  trouserLengthOptions,
  vehicleTypeOptions,
} from '../types/alarm-consts'

type CriterionConfig = {
  label: string
  icon: string
  component: 'color' | 'text' | 'boolean' | 'dropdown'
  options?: Array<{ label: string; value: string }>
  placeholder?: string
  maxLength?: number
}

export const AHCR_CRITERION_CONFIG: Record<AhcrCriterionTypeEnum, CriterionConfig> = {
  [AhcrCriterionTypeEnum.JACKET_COLOR]: {
    label: 'Cor da Jaqueta',
    icon: 'pi pi-palette',
    component: 'color',
  },
  [AhcrCriterionTypeEnum.TROUSER_COLOR]: {
    label: 'Cor da Calça',
    icon: 'pi pi-palette',
    component: 'color',
  },
  [AhcrCriterionTypeEnum.HAT]: {
    label: 'Chapéu',
    icon: 'pi pi-sun',
    component: 'boolean',
  },
  [AhcrCriterionTypeEnum.BAG]: {
    label: 'Mochila',
    icon: 'pi pi-briefcase',
    component: 'boolean',
  },
  [AhcrCriterionTypeEnum.GENDER]: {
    label: 'Gênero',
    icon: 'pi pi-users',
    component: 'dropdown',
    options: genderOptions,
  },
  [AhcrCriterionTypeEnum.MASK]: {
    label: 'Máscara',
    icon: 'pi pi-shield',
    component: 'boolean',
  },
  [AhcrCriterionTypeEnum.SLEEVE_LENGTH]: {
    label: 'Comprimento da Manga',
    icon: 'pi pi-th-large',
    component: 'dropdown',
    options: sleeveLengthOptions,
  },
  [AhcrCriterionTypeEnum.TROUSER_LENGTH]: {
    label: 'Comprimento da Calça',
    icon: 'pi pi-th-large',
    component: 'dropdown',
    options: trouserLengthOptions,
  }
}

export const ANPR_CRITERION_CONFIG: Record<AnprCriterionTypeEnum, CriterionConfig> = {
  [AnprCriterionTypeEnum.LICENSE_PLATE]: {
    label: 'Placa do Veículo',
    icon: 'pi pi-id-card',
    component: 'text',
    placeholder: 'Ex: ABC1234',
    maxLength: 7,
  },
  [AnprCriterionTypeEnum.COLOR]: {
    label: 'Cor do Veículo',
    icon: 'pi pi-palette',
    component: 'color',
  },
  [AnprCriterionTypeEnum.TYPE]: {
    label: 'Tipo de Veículo',
    icon: 'pi pi-car',
    component: 'dropdown',
    options: vehicleTypeOptions
  },
}

export const getCriterionConfig = (
  category: 'ANPR' | 'AHCR',
  type: string
): CriterionConfig | undefined => {
  if (category === 'ANPR') {
    return ANPR_CRITERION_CONFIG[type as AnprCriterionTypeEnum]
  }
  return AHCR_CRITERION_CONFIG[type as AhcrCriterionTypeEnum]
}

export const getCriterionTypeLabel = (type: string, withColon = true): string => {
  const config =
    ANPR_CRITERION_CONFIG[type as AnprCriterionTypeEnum] ??
    AHCR_CRITERION_CONFIG[type as AhcrCriterionTypeEnum]

  const label = config?.label ?? type
  return withColon ? `${label}:` : label
}

const BOOLEAN_LABELS: Record<string, string> = {
  Yes: 'Sim',
  No: 'Não',
}

export const getCriterionDisplayValue = (value: string): string => {
  // Check boolean labels
  if (value in BOOLEAN_LABELS) {
    return BOOLEAN_LABELS[value]
  }

  // Check gender options
  const genderMatch = genderOptions.find((opt) => opt.value === value)
  if (genderMatch) return genderMatch.label

  // Check sleeve length options
  const sleeveMatch = sleeveLengthOptions.find((opt) => opt.value === value)
  if (sleeveMatch) return sleeveMatch.label

  // Check trouser length options
  const trouserMatch = trouserLengthOptions.find((opt) => opt.value === value)
  if (trouserMatch) return trouserMatch.label

  // Check vehicle type options
  const vehicleMatch = vehicleTypeOptions.find((opt) => opt.value === value)
  if (vehicleMatch) return vehicleMatch.label

  // Check color options
  const colorMatch = alarmColors.find((color) => color.value === value)
  if (colorMatch) return colorMatch.name

  return value
}
